import { defineMessages } from "./define.js";

/** The wishes screen: the add form, a wish card, its statuses and the preview. */
export const wishes = defineMessages("wishes", {
  title: { ru: "Хотелки", en: "Wishes" },
  subtitle: {
    ru: "Что хочется — со ссылкой или просто словами.",
    en: "What you'd like — with a link or just in words.",
  },
  empty: {
    ru: "Пока пусто. Добавьте первую — ссылка не обязательна.",
    en: "Nothing here yet. Add the first one — a link is optional.",
  },
  settled: { ru: "Уже случилось", en: "Already happened" },

  titlePlaceholder: { ru: "Что хочется", en: "What you'd like" },
  titleLabel: { ru: "Название хотелки", en: "Wish title" },
  /** Имя кнопки «+» в заголовке экрана — она без надписи. */
  newWish: { ru: "Новая хотелка", en: "New wish" },
  urlLabel: { ru: "Ссылка", en: "Link" },
  /* Хотелка обычно начинается в другом приложении: ссылку скопировали в
     браузере, переключились сюда — и дальше долгое нажатие и системное меню. */
  pasteLink: { ru: "Вставить ссылку", en: "Paste the link" },
  urlHint: {
    ru: "Необязательно — превью подтянется само, если сайт его отдаёт",
    en: "Optional — the preview loads itself, if the site hands one over",
  },

  /* ------------------------------------------------------------------ sheet

     The card is a name and who took it on; everything else about a wish —
     its own words, its files, its tags, its editing — lives on the sheet. */
  details: { ru: "Хотелка", en: "Wish" },
  /** The card's own label, so eleven rows are not eleven «Открыть». */
  detailsOf: { ru: "Хотелка: «{title}»", en: "Wish: “{title}”" },

  /* The sheet has two states and one header. Opening a wish shows it; the
     pencil turns the same sheet into the form and «Готово» hands it back. */
  edit: { ru: "Редактировать", en: "Edit" },
  editTitle: { ru: "Правка", en: "Editing" },
  done: { ru: "Готово", en: "Done" },

  /** The visible label on the edit form — shorter than `titleLabel`, which is
      an invisible one for the add field and has to name what it belongs to. */
  fieldTitle: { ru: "Название", en: "Title" },
  fieldPrice: { ru: "Цена", en: "Price" },
  // Written as words, not parsed as a number: «около 3 000», «$40 + доставка»
  // and «3 490 ₽» are all things people actually put here.
  pricePlaceholder: { ru: "3 490 ₽", en: "£34.90" },
  fieldNote: { ru: "Заметка", en: "Note" },
  notePlaceholder: {
    ru: "Размер, цвет, что важно",
    en: "Size, colour, what matters",
  },

  /** Both the picture on the card and the preview on the sheet say this. */
  openLink: {
    ru: "Открыть {host} в новой вкладке",
    en: "Open {host} in a new tab",
  },

  /* The card counts what it no longer lists. An icon and a number take a
     quarter of the line a row of chips took, and the chips themselves are one
     tap away on the sheet. */
  tagCount: {
    ru: { one: "{count} тег", few: "{count} тега", many: "{count} тегов", other: "{count} тега" },
    en: { one: "{count} tag", other: "{count} tags" },
  },
  fileCount: {
    ru: {
      one: "{count} файл",
      few: "{count} файла",
      many: "{count} файлов",
      other: "{count} файла",
    },
    en: { one: "{count} file", other: "{count} files" },
  },

  // The status a wish is in, and the button that moves it there — one wording
  // for both, the way the screen already used one constant for both.
  statusIdea: { ru: "Хочется", en: "Want it" },
  statusBought: { ru: "Куплено", en: "Bought" },
  statusGifted: { ru: "Подарено", en: "Gifted" },

  claim: { ru: "Возьму на себя", en: "I'll get it" },
  claimed: { ru: "Беру на себя", en: "I'm on it" },
  /* Нейтрально по роду: имя не говорит, «добавил» это или «добавила». */
  /* Подпись для наведения и скринридера: на самой карточке стоит одно имя в
     именительном, потому что «от {name}» русский склоняет, а имя не говорит
     как — на экране было «от Танечка». */
  addedByWhom: { ru: "Кто добавил: {name}", en: "Added by {name}" },
  discussAbout: { ru: "Обсудить «{title}»", en: "Discuss “{title}”" },
  claimedByYou: { ru: "вы взяли на себя", en: "you're getting it" },
  claimedBySomeone: { ru: "{name} взял(а) на себя", en: "{name} is getting it" },

  remove: { ru: "Удалить «{title}»", en: "Delete “{title}”" },
  // The second press. `common.confirmDelete` is what the button says; this is
  // what a screen reader announces, because the title is the only thing that
  // tells eleven identical trash buttons apart.
  removeConfirm: {
    ru: "Точно удалить «{title}»?",
    en: "Delete “{title}” for good?",
  },
  unfurling: { ru: "Загружаем превью…", en: "Loading the preview…" },
  refreshPreview: { ru: "Обновить превью", en: "Refresh preview" },
  refreshPreviewFor: {
    ru: "Обновить превью для «{title}»",
    en: "Refresh the preview for “{title}”",
  },
  imageBroken: { ru: "Картинка не открылась", en: "The picture didn't load" },

  /* ----------------------------------------------------------------- filters

     Three questions, and the row says which one each group of pills answers —
     «я» means two different things depending on the group, and a pill on its
     own cannot say which. */
  filterStatus: { ru: "Статус", en: "Status" },
  filterClaim: { ru: "Кто взял", en: "Who's on it" },
  filterAuthor: { ru: "Кто завёл", en: "Who added it" },

  filterAll: { ru: "Все", en: "All" },
  filterMine: { ru: "Я", en: "Me" },
  // The name is what is actually shown; this is the stand-in for a space where
  // the second person has not joined yet.
  filterPartner: { ru: "Второй участник", en: "The other one" },
  filterUnclaimed: { ru: "Никто", en: "Nobody" },
  /* Теги можно было поставить, и дальше они ничего не делали. */
  filterTag: { ru: "Тег", en: "Tag" },

  /** Only shown while something is filtered out — «3 из 3» tells nobody anything. */
  filterCount: {
    ru: {
      one: "{count} хотелка из {total}",
      few: "{count} хотелки из {total}",
      many: "{count} хотелок из {total}",
      other: "{count} хотелок из {total}",
    },
    en: { one: "{count} wish of {total}", other: "{count} wishes of {total}" },
  },

  filterNothing: {
    ru: "Под эти фильтры ничего не попало.",
    en: "Nothing matches these filters.",
  },
  filterReset: { ru: "Сбросить фильтры", en: "Reset the filters" },
  /** What the other person's lock screen says. Short: a push caps at 4KB. */
  /* Кто, а не «новая хотелка»: последнее слово в слово совпадало с нейтральной
     строкой, которую показывают, когда устройство пуш прочитать *не смогло*, —
     то есть работающая расшифровка выглядела как несработавшая. */
  notifyAdded: { ru: "{name}: новая хотелка", en: "{name}: a new wish" },
  discuss: { ru: "Обсудить", en: "Discuss" },
  discussionCount: {
    ru: {
      one: "Обсуждение · {count} сообщение",
      few: "Обсуждение · {count} сообщения",
      many: "Обсуждение · {count} сообщений",
      other: "Обсуждение · {count} сообщения",
    },
    en: { one: "Discussion · {count} message", other: "Discussion · {count} messages" },
  },
});
